import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { MapContainer, CircleMarker, Popup, Polyline, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { ArrowLeft, MapPin, FireSimple, Compass, Calendar, Users, PaperPlaneTilt, VideoCamera, Star, Check, ShieldCheck, SignIn, Heart, TrendUp, Camera, X, CircleNotch, FileText, PencilSimple, Trash, MapTrifold, Lightning, Broadcast } from "@phosphor-icons/react";
import { StatusBadge, TypeBadge } from "../components/Badges";
import CameraCard from "../components/CameraCard";
import CameraViewerModal from "../components/CameraViewerModal";
import SponsorRotator from "../components/SponsorRotator";
import MapBaseLayers from "../components/map/MapBaseLayers";
import useDetailMapExtras from "../components/map/useDetailMapExtras";
import PerimeterLayer from "../components/map/PerimeterLayer";
import ViirsHotspotsLayer from "../components/map/ViirsHotspotsLayer";
import SheriffEvacLayer from "../components/map/SheriffEvacLayer";
import EvacClosuresStrip from "../components/map/EvacClosuresStrip";
import SheriffEvacCard from "../components/SheriffEvacCard";
import EmRoadClosuresCard from "../components/EmRoadClosuresCard";
import RoadClosuresLayer from "../components/map/RoadClosuresLayer";
import InlineShareButton from "../components/InlineShareButton";
import FollowFireButton from "../components/FollowFireButton";
import ScannerFeedsCard from "../components/ScannerFeedsCard";
import SpreadAnalysisCard from "../components/SpreadAnalysisCard";
import SizeHistoryBox from "../components/SizeHistoryBox";
import WeatherPanel from "../components/WeatherPanel";
import FireWeatherOutlook from "../components/FireWeatherOutlook";
import DebrisFlowNotice from "../components/DebrisFlowNotice";
import ZoomLabelScaler from "../components/map/ZoomLabelScaler";
import YourLocationPanel, { loadHomePin } from "../components/YourLocationPanel";
import { api, fetchRoadAlerts } from "../lib/api";
import { getOrCreateClientId } from "../lib/pushClient";
import { distMi } from "../lib/followedFires";
import { shareOrigin } from "../lib/shareOrigin";
import { buildIncidentCaption, evacsForShare } from "../lib/shareCaption";
import {
  fetchIncident, fetchUpdates, postUpdate, fetchIncidentCameras, uploadFieldImage, fileUrl, fetchIncidentPhotos,
  fetchNifcIncidents, fetchAcresHistory, fetchGrowthEstimate, fetchNwccBrief, trackDonateClick, fetchSpreadAnalysis,
  fetchViirsHotspots, fetchEvacAlerts, fetchRoadClosures, fetchIncidents,
} from "../lib/api";
import { toast } from "sonner";
import { fetchFirePerimeters } from "../lib/proximity";
import { sameFire, matchPerimeter, bestFireAcres, bestWildcadMatch } from "../lib/dedupe";
import { formatAcres, formatDate, relTime } from "../lib/incidents";
import { useSavedLocations } from "../lib/SavedLocationsContext";
import { useAuth } from "../lib/AuthContext";

function toParagraphs(text) {
  if (!text) return [];
  const parts = text.split(/\n{1,2}/).map((s) => s.trim()).filter(Boolean);
  const out = [];
  for (const part of parts) {
    if (part.length > 300) {
      const sentences = part.match(/[^.!?]+[.!?]+["')\]]*\s*|[^.!?]+$/g) || [part];
      let buf = [];
      for (const s of sentences) {
        buf.push(s.trim());
        if (buf.length >= 3) {
          out.push(buf.join(" "));
          buf = [];
        }
      }
      if (buf.length) out.push(buf.join(" "));
    } else {
      out.push(part);
    }
  }
  return out;
}


export default function IncidentDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { locations, add } = useSavedLocations();
  const { user } = useAuth();
  const [incident, setIncident] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [cameras, setCameras] = useState([]);
  const [nearbyPerimeters, setNearbyPerimeters] = useState([]);
  const [nearbyViirs, setNearbyViirs] = useState([]);
  const [allViirs, setAllViirs] = useState([]);
  const [showAllHotspots, setShowAllHotspots] = useState(false);
  const [nearbyEvacs, setNearbyEvacs] = useState([]);
  const [nearbyClosures, setNearbyClosures] = useState([]);
  const [nifcMatch, setNifcMatch] = useState(null);
  const [perimMatch, setPerimMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [photoPins, setPhotoPins] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchIncidentPhotos(id).then(setPhotoPins).catch(() => {});
  }, [id]);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [deepLinkedCamera, setDeepLinkedCamera] = useState(null);
  const [growth, setGrowth] = useState(null);
  const [spread, setSpread] = useState(null);
  const [homePin, setHomePin] = useState(() => loadHomePin());
  const [isFollowing, setIsFollowing] = useState(false);
  const [wsdotAlerts, setWsdotAlerts] = useState([]);
  const [nwccMatch, setNwccMatch] = useState(null);
  const [showOldViirs, setShowOldViirs] = useState(false);
  const [showEvacZones, setShowEvacZones] = useState(true);
  const mapExtras = useDetailMapExtras(incident?.county);
  const [evacFocus, setEvacFocus] = useState(null);
  const [popupEvac, setPopupEvac] = useState(null);
  const [stripFocus, setStripFocus] = useState(null);
  const [lastSharedAt, setLastSharedAt] = useState(null);
  const [lightningFlag, setLightningFlag] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);

  // Share caption summary: use any cached narrative for free; if none exists
  // and the viewer is logged in (staff/members do the sharing), generate one
  // in the background so it's ready by the time they hit Share.
  useEffect(() => {
    let stale = false;
    api
      .get(`/incidents/${id}/ai-summary`, { params: { cached_only: true } })
      .then((r) => {
        if (stale) return;
        if (r.data?.summary) setAiSummary(r.data.summary);
        else if (user && user.role) {
          api
            .get(`/incidents/${id}/ai-summary`)
            .then((r2) => { if (!stale && r2.data?.summary) setAiSummary(r2.data.summary); })
            .catch(() => {});
        }
      })
      .catch(() => {});
    return () => { stale = true; };
  }, [id, user]);

  useEffect(() => {
    api
      .get("/lightning-fires")
      .then((r) => setLightningFlag((r.data.flags || {})[id] || null))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    api
      .get(`/metrics/share-log/${id}`)
      .then((r) => setLastSharedAt(r.data.last_shared_at || null))
      .catch(() => {});
    const onShared = (e) => {
      if (e.detail?.incident_id === id) setLastSharedAt(e.detail.at);
    };
    window.addEventListener("fw-share-logged", onShared);
    return () => window.removeEventListener("fw-share-logged", onShared);
  }, [id]);

  useEffect(() => {
    fetchRoadAlerts("all").then(setWsdotAlerts).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get(`/push/follows/${getOrCreateClientId()}`)
      .then((r) => setIsFollowing((r.data.incident_ids || []).includes(id)))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    fetchGrowthEstimate(id).then(setGrowth).catch(() => {});
  }, [id]);

  useEffect(() => {
    fetchSpreadAnalysis(id, nifcMatch?.id).then(setSpread).catch(() => {});
  }, [id, nifcMatch?.id]);

  useEffect(() => {
    if (!incident?.name) return;
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    fetchNwccBrief()
      .then((d) => {
        if (!d?.available) return;
        const hit = (d.incidents || []).find((f) => norm(f.name) === norm(incident.name));
        if (hit) setNwccMatch(hit);
      })
      .catch(() => {});
  }, [incident?.name]);

  useEffect(() => {
    const run = async () => {
      try {
        const [inc, ups, cams, perims, viirs, evacs, closures, nifc] = await Promise.all([
          fetchIncident(id),
          fetchUpdates(id),
          fetchIncidentCameras(id, 4).catch(() => []),
          fetchFirePerimeters("all", true).catch(() => []),
          fetchViirsHotspots({ hours: 48 }).catch(() => []),
          fetchEvacAlerts({ include_historical: true }).catch(() => []),
          fetchRoadClosures().catch(() => []),
          fetchNifcIncidents({}).catch(() => []),
        ]);
        setIncident(inc);
        // Stale-duplicate guard: if this record duplicates a better WildCAD
        // record for the same fire (e.g. a leftover "Miscellaneous" entry),
        // redirect so members never post/read on the wrong page.
        if (inc?.name) {
          try {
            const siblings = await fetchIncidents({ county: inc.county, days: 14 });
            const best = bestWildcadMatch(siblings || [], inc);
            if (best && best.id !== inc.id) {
              toast.info(`Redirected to the main record for ${best.name}`);
              navigate(`/incident/${best.id}`, { replace: true });
              return;
            }
          } catch { /* duplicates check is best-effort */ }
        }
        setUpdates(ups);
        setCameras(cams);
        setAllViirs(viirs || []);
        // Federal (NIFC) record for the same fire — often has better acreage
        // and containment % than the raw WildCAD dispatch entry.
        if (inc?.latitude != null && inc?.longitude != null) {
          const match = (nifc || []).find((n) => sameFire(inc, n));
          setNifcMatch(match || null);
          setPerimMatch(matchPerimeter(inc, perims || []));
        }
        // Filter perimeters + VIIRS to those within ~10 mi of the incident.
        if (inc?.latitude != null && inc?.longitude != null) {
          // Bounding-box check (10 mi ≈ 0.145° lat, ~0.22° lon at 48°N).
          const latTol = 0.16;
          const lonTol = 0.22;
          setNearbyPerimeters(
            (perims || []).filter((p) => {
              if (!p.geometry) return false;
              // Best-effort centroid via incident_lat/incident_lon if present;
              // otherwise just trust geometry intersects when within bbox.
              const lat = p.latitude ?? p.incident_lat;
              const lon = p.longitude ?? p.incident_lon;
              if (lat == null || lon == null) return true; // can't filter, include
              return (
                Math.abs(lat - inc.latitude) <= latTol &&
                Math.abs(lon - inc.longitude) <= lonTol
              );
            }),
          );
          setNearbyViirs(
            (viirs || []).filter(
              (h) =>
                Math.abs(h.latitude - inc.latitude) <= latTol &&
                Math.abs(h.longitude - inc.longitude) <= lonTol,
            ),
          );
          // Evac zones: strictly this fire's zones only (same matcher the
          // share caption uses) — no county-wide or nearby-area grouping.
          const strictIds = new Set(
            evacsForShare(evacs || [], inc.latitude, inc.longitude, inc.name).map((e) => e.id),
          );
          setNearbyEvacs((evacs || []).filter((a) => strictIds.has(a.id)));
          // County EM road closures: within ~25 mi of the incident
          setNearbyClosures(
            (closures || []).filter(
              (c) =>
                Math.abs(c.latitude - inc.latitude) <= 0.4 &&
                Math.abs(c.longitude - inc.longitude) <= 0.55,
            ),
          );
        }
      } catch (e) {
        setError("Incident not found");
      } finally {
        setLoading(false);
      }
    };
    run();
    // Keep evacs/perimeters fresh while the page stays open — a share made
    // from a stale tab must still carry the newest evac info.
    const t = setInterval(run, 2 * 60 * 1000);
    return () => clearInterval(t);
  }, [id]);

  // Deep-link from a push notification: /incidents/{id}?camera={cam_id}
  useEffect(() => {
    const camParam = searchParams.get("camera");
    if (!camParam || cameras.length === 0) return;
    const match = cameras.find((c) => c.id === camParam);
    if (match) setDeepLinkedCamera(match);
    // Clean the query param so refresh doesn't re-open the modal
    searchParams.delete("camera");
    setSearchParams(searchParams, { replace: true });
  }, [cameras, searchParams, setSearchParams]);

  const canAttach = user && user !== false && (user.verified || user.role === "admin");

  const getDeviceCoords = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (p) => {
          const { latitude: lat, longitude: lon, accuracy } = p.coords;
          // Network-based geolocation (satellite internet, VPN) often reports
          // the ISP gateway hundreds of miles away — don't geotag with it.
          if (accuracy > 10000) return resolve(null);
          if (lat < 45.0 || lat > 50.5 || lon < -125.5 || lon > -116.5) return resolve(null);
          resolve({ lat, lon });
        },
        () => resolve(null),
        { timeout: 4000, maximumAge: 300000 },
      );
    });

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - attachments.length);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    setPostError(null);
    const coords = await getDeviceCoords();
    for (const f of files) {
      try {
        const res = await uploadFieldImage(f, coords);
        setAttachments((prev) =>
          prev.length < 4 ? [...prev, { id: res.id, name: f.name, geotagged: res.latitude != null }] : prev,
        );
      } catch (err) {
        setPostError(err.response?.data?.detail || "Image upload failed. Try again.");
      }
    }
    setUploading(false);
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setPosting(true);
    setPostError(null);
    try {
      const u = await postUpdate(id, { message: message.trim(), image_ids: attachments.map((a) => a.id) });
      setUpdates((prev) => [u, ...prev]);      if (attachments.length) fetchIncidentPhotos(id).then(setPhotoPins).catch(() => {});
      setMessage("");
      setAttachments([]);
    } catch (err) {
      if (err.response?.status === 401) {
        setPostError("Your session expired. Please log in again.");
      } else {
        setPostError("Could not post update. Try again in a moment.");
      }
    } finally {
      setPosting(false);
    }
  };

  const toggleHeart = async (updateId) => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(`/incident/${id}`)}`);
      return;
    }
    try {
      const r = await api.post(`/incidents/${id}/updates/${updateId}/heart`);
      setUpdates((prev) => prev.map((x) => (x.id === updateId ? r.data : x)));
    } catch {
      /* noop */
    }
  };

  const deleteUpdate = async (updateId) => {
    if (!window.confirm("Delete this field update? This can't be undone.")) return;
    try {
      await api.delete(`/incidents/${id}/updates/${updateId}`);
      setUpdates((prev) => prev.filter((x) => x.id !== updateId));
    } catch (err) {
      setPostError(
        err.response?.status === 403
          ? "Your session expired — log in again to delete."
          : "Could not delete the update. Try again in a moment.",
      );
    }
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    try {
      const r = await api.patch(`/incidents/${id}/updates/${editingId}`, { message: editText.trim() });
      setUpdates((prev) => prev.map((x) => (x.id === editingId ? r.data : x)));
      setEditingId(null);
      setEditText("");
    } catch (err) {
      setPostError(
        err.response?.status === 403
          ? "Your session expired — log in again to edit."
          : "Could not save the edit. Try again in a moment.",
      );
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-56px)] gap-4">
        <div className="scan-line w-64" />
        <div className="text-[15px] font-mono-tac uppercase tracking-widest text-tac-dim">Loading incident…</div>
      </div>
    );
  }
  if (error || !incident) {
    return (
      <div className="min-h-[calc(100vh-56px)] flex flex-col items-center justify-center text-center px-4">
        <div className="text-tac-faint font-mono-tac text-[15px] uppercase tracking-widest mb-2">404</div>
        <h2 className="font-display font-bold text-2xl text-tac-text mb-3">Incident Not Found</h2>
        <p className="text-[15px] text-tac-dim mb-6">This incident may have been removed or its ID is invalid.</p>
        <Link to="/" data-testid="back-home-link" className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-tac-primary text-white rounded-sm hover:bg-tac-primary/90">
          Back to Live Map
        </Link>
      </div>
    );
  }

  const fs = incident.fire_status || {};

  // Best-known acreage: mapped GIS perimeter > NIFC point > WildCAD dispatch.
  const best = bestFireAcres(incident, nifcMatch, perimMatch);
  const nifcAcresVal = nifcMatch?.incident_size;
  const bestAcresDisplay = best
    ? best.source === "mapped"
      ? `${formatAcres(best.acres)} (mapped${
          nifcAcresVal != null && Math.round(nifcAcresVal) !== Math.round(best.acres)
            ? ` · NIFC ${formatAcres(nifcAcresVal)}`
            : ""
        })`
      : `${formatAcres(best.acres)}${best.source === "nifc" ? " (NIFC)" : ""}`
    : formatAcres(incident.acres);
  const coordsValid = incident.latitude != null && incident.longitude != null;
  const recentViirs = nearbyViirs.filter(
    (h) => h.acq_datetime && Date.now() - new Date(h.acq_datetime).getTime() <= 24 * 36e5,
  );
  const oldViirsCount = nearbyViirs.length - recentViirs.length;

  return (
    <div data-testid="incident-detail" className="incident-panel max-w-6xl mx-auto px-4 sm:px-6 py-6 animate-fade-in">
      <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 text-[15px] uppercase tracking-wider text-tac-dim hover:text-tac-primary mb-5 transition-colors">
        <ArrowLeft size={14} weight="bold" /> Back to Live Map
      </Link>

      {/* Donate ask — community-funded service */}
      <div
        data-testid="incident-donate-strip"
        className="border border-tac-primary/40 bg-tac-primary/10 mb-5 px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2">
          <Heart size={16} weight="fill" className="text-tac-primary shrink-0" />
          <span className="text-sm text-tac-text font-medium">
            Help us Keep the App free for Everyone. Please Donate
          </span>
        </div>
        <Link
          to="/donate"
          data-testid="incident-donate-btn"
          onClick={() => trackDonateClick("incident")}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold uppercase tracking-wider bg-tac-primary text-white rounded-sm hover:opacity-90 transition-opacity"
        >
          <Heart size={12} weight="fill" /> Donate
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <TypeBadge type={incident.type} />
            <StatusBadge status={incident.status_label} />
            {incident.satellite_heat && (
              <span
                data-testid="sat-heat-badge-detail"
                title={`NASA VIIRS detected ${incident.satellite_heat.count} heat point${incident.satellite_heat.count === 1 ? "" : "s"} within 4 mi in the last 6 hours`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-orange-400/70 bg-orange-400/15 text-orange-400"
              >
                <Broadcast size={10} weight="fill" /> Satellite heat confirmed
              </span>
            )}
            {incident.fire_num && (
              <span className="text-[15px] font-mono-tac tabular text-tac-faint uppercase tracking-wider">
                Fire# {incident.fire_num}
              </span>
            )}
            {incident.inc_num && (
              <span className="text-[15px] font-mono-tac tabular text-tac-faint uppercase tracking-wider">
                Inc# {incident.inc_num}
              </span>
            )}
            {incident.county && (
              <span className="text-[15px] font-bold uppercase tracking-wider text-tac-dim">
                · {incident.county.replace("_", " ")} Co.
              </span>
            )}
            {lightningFlag && (
              <span
                data-testid="incident-lightning-flag"
                title="Possible lightning-caused start"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold uppercase tracking-wider border border-[#7DD3FC]/70 bg-[#7DD3FC]/15 text-[#7DD3FC]"
              >
                <Lightning size={11} weight="fill" /> Lightning near start ·{" "}
                {lightningFlag.strikes} strike{lightningFlag.strikes === 1 ? "" : "s"} ≤3 mi
              </span>
            )}
          </div>
          <h1 data-testid="incident-name" className="font-display font-black text-3xl sm:text-4xl tracking-tight text-tac-text leading-tight">
            {incident.name}
          </h1>
          <div className="mt-2 text-[15px] text-tac-dim font-mono-tac">
            {incident.dispatch_center_name} · {relTime(incident.date)}
          </div>
        </div>
        {coordsValid && (
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/?flyto=${incident.latitude},${incident.longitude}&inc=${incident.id}`}
              data-testid="view-on-map-button"
              title="Jump to this fire on the live map"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold uppercase tracking-wider border border-tac-primary/60 rounded-sm text-tac-primary hover:bg-tac-primary/10 transition-colors"
            >
              <MapTrifold size={13} weight="bold" /> View on Map
            </Link>
            <FollowFireButton incidentId={incident.id} onChange={setIsFollowing} />
            <a
              href={`/incident/${incident.id}/summary`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="incident-summary-button"
              title="AI-written narrative summary — opens in a new window"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold uppercase tracking-wider border border-tac-border rounded-sm text-tac-dim hover:text-tac-text hover:border-tac-borderBright transition-colors"
            >
              <FileText size={13} weight="bold" /> Summary
            </a>
            <InlineShareButton
              testid="incident-share-button"
              url={`${shareOrigin()}/api/share/incident/${incident.id}`}
              previewPath={`/incident/${incident.id}/share`}
              title={`${incident.name} — Firewatch`}
              caption={buildIncidentCaption({
                name: incident.name,
                county: incident.county,
                counties: perimMatch?.counties,
                acres: best?.acres ?? incident.acres,
                contained: nifcMatch?.percent_contained,
                statusLabel: incident.status_label,
                incNum: incident.inc_num,
                resources: incident.resources,
                evacs: evacsForShare(
                  nearbyEvacs.filter((a) => a.active !== false),
                  incident.latitude,
                  incident.longitude,
                  incident.name
                ),
                updatedAt: incident.date,
                url: `${shareOrigin()}/api/share/incident/${incident.id}`,
                lastSharedAt,
                summary: aiSummary,
                onlyNewEvacs: true,
              })}
              text={
                `${incident.type || "Incident"} · ${incident.name}` +
                (incident.acres != null ? ` · ${incident.acres} ac` : "") +
                (incident.county ? ` · ${incident.county.replace("_", " ")} Co.` : "") +
                " · via Okanogan Highlands Firewatch"
              }
            />
            {lastSharedAt && (
              <span
                data-testid="shared-indicator-detail"
                title={`This fire was last shared ${relTime(lastSharedAt)}`}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border border-tac-safe/60 bg-tac-safe/10 text-tac-safe"
              >
                <Check size={12} weight="bold" /> Shared {relTime(lastSharedAt)}
              </span>
            )}
            <button
              data-testid="save-incident-location"
              onClick={() => {
                add({
                  label: `Near ${incident.name}`,
                  lat: incident.latitude,
                  lon: incident.longitude,
                  radius_mi: 10,
                });
                setSaved(true);
              }}
              disabled={saved}
              className={`flex items-center gap-2 px-3 h-9 text-[10px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                saved
                  ? "bg-tac-safe/15 text-tac-safe border-tac-safe/40 cursor-default"
                  : "bg-tac-surface text-tac-text border-tac-border hover:border-tac-primary hover:bg-tac-surface2"
              }`}
            >
              {saved ? (
                <><Check size={12} weight="bold" /> Saved · {locations.length}</>
              ) : (
                <><Star size={12} weight="fill" className="text-tac-primary" /> Watch this area</>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 border border-tac-border mb-6 bg-tac-surface/40">
        <Stat
          icon={FireSimple}
          label="Acres"
          value={bestAcresDisplay}
          note={best?.source === "mapped" ? "mapped = latest GIS perimeter drawn around the fire" : null}
        />
        {nifcMatch?.percent_contained != null ? (
          <Stat icon={ShieldCheck} label="Contained (NIFC)" value={`${nifcMatch.percent_contained}%`} />
        ) : (
          <Stat icon={Compass} label="Fuels" value={incident.fuels || "—"} />
        )}
        <Stat icon={MapPin} label="Location" value={incident.location || "—"} mono />
        <Stat icon={Calendar} label="Reported" value={formatDate(incident.date)} />
      </div>

      {growth?.available && growth?.significant && (
        <GrowthEstimateStrip growth={growth} nifcAcres={nifcMatch?.incident_size} />
      )}

      <SpreadAnalysisCard spread={spread} />

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <YourLocationPanel
          fireLat={incident.latitude}
          fireLon={incident.longitude}
          fireName={incident.name}
          spread={spread}
          pin={homePin}
          onPinChange={setHomePin}
        />
        <WeatherPanel lat={incident.latitude} lon={incident.longitude} />
        <DebrisFlowNotice fireName={incident.name} />
        <FireWeatherOutlook lat={incident.latitude} lon={incident.longitude} />
      </div>

      {nwccMatch?.team && <TeamsOnGroundStrip match={nwccMatch} />}

      {nifcMatch && <NifcFederalCard nifc={nifcMatch} incidentId={incident.id} />}

      <div className="grid gap-4 mb-6 lg:grid-cols-[300px_1fr]">
        <div className="space-y-4 self-start">
          {(nearbyEvacs.length > 0 || nearbyClosures.length > 0) && (
            <div
              data-testid="incident-evac-notices"
              className="border border-tac-danger/40 rounded-sm overflow-hidden"
            >
              <SheriffEvacCard
                alerts={nearbyEvacs}
                closures={nearbyClosures}
                wsdotAlerts={wsdotAlerts}
                scrollList
                onFlyTo={(lat, lon, id) => id && setEvacFocus({ id, ts: Date.now() })}
              />
              <EmRoadClosuresCard closures={nearbyClosures} />
            </div>
          )}
          <div className="border border-tac-border bg-tac-surface/40 p-4" data-testid="operational-status-box">
            <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-3">
              Operational Status
            </div>
            <div className="space-y-3">
              <StatusRow label="Contained" value={fs.contain} />
              <StatusRow label="Controlled" value={fs.control} />
              <StatusRow label="Out" value={fs.out} />
            </div>
            {incident.ic && (
              <div className="mt-5 pt-4 border-t border-tac-border">
                <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-1.5">Incident Commander</div>
                <div className="flex items-center gap-2 text-sm text-tac-text">
                  <Users size={14} className="text-tac-primary" weight="fill" />
                  <span>{incident.ic}</span>
                </div>
              </div>
            )}
            {coordsValid && (
              <div className="mt-4 pt-4 border-t border-tac-border">
                <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-1.5">Coordinates</div>
                <div className="font-mono-tac tabular text-sm text-tac-text">
                  {incident.latitude.toFixed(5)}°, {incident.longitude.toFixed(5)}°
                </div>
              </div>
            )}
          </div>
          <SizeHistoryBox
            incidentId={incident.id}
            nifcId={nifcMatch?.id}
            perimId={perimMatch?.id}
          />
        </div>
        <div data-testid="detail-map" className="relative border border-tac-border rounded-sm overflow-hidden h-[420px] lg:h-[560px] bg-tac-surface">
          {coordsValid ? (
            <MapContainer
              center={[incident.latitude, incident.longitude]}
              zoom={11}
              className="h-full w-full"
              style={{ background: "#0F1311" }}
            >
              <MapBaseLayers />
              <ZoomLabelScaler />
              {mapExtras.layers}
              {showEvacZones && <SheriffEvacLayer alerts={nearbyEvacs.filter((a) => a.active !== false)} dedupeMarkers subtle focusEvac={evacFocus} onPopupEvac={setPopupEvac} />}
              {stripFocus && <PanTo position={stripFocus} />}
              <RoadClosuresLayer closures={nearbyClosures} />
              <PerimeterLayer perimeters={nearbyPerimeters} />
              <ViirsHotspotsLayer
                hotspots={showAllHotspots ? allViirs : showOldViirs ? nearbyViirs : recentViirs}
                colorByAge
              />
              <CircleMarker
                center={[incident.latitude, incident.longitude]}
                radius={12}
                pathOptions={{ color: "#0A0D0C", weight: 2, fillColor: "#F2542D", fillOpacity: 1 }}
              />
              {isFollowing && (
                <CircleMarker
                  center={[incident.latitude, incident.longitude]}
                  radius={19}
                  pathOptions={{ color: "#F2542D", weight: 2.5, fill: false, className: "followed-pulse" }}
                />
              )}
              {homePin && (
                <>
                  <FitToPin fire={[incident.latitude, incident.longitude]} pin={homePin} />
                  <CircleMarker
                    center={[homePin.lat, homePin.lon]}
                    radius={8}
                    pathOptions={{ color: "#FFFFFF", weight: 2, fillColor: "#4CC9F0", fillOpacity: 1 }}
                  >
                    <Tooltip direction="top">{homePin.label}</Tooltip>
                  </CircleMarker>
                  <Polyline
                    positions={[[homePin.lat, homePin.lon], [incident.latitude, incident.longitude]]}
                    pathOptions={{ color: "#4CC9F0", weight: 2, opacity: 0.8, dashArray: "4 8" }}
                  >
                    <Tooltip permanent direction="center" className="followed-dist-label">
                      {distMi(homePin.lat, homePin.lon, incident.latitude, incident.longitude).toFixed(1)} mi
                    </Tooltip>
                  </Polyline>
                </>
              )}
              {cameras.map((cam) => (
                <CircleMarker
                  key={cam.id}
                  center={[cam.lat, cam.lon]}
                  radius={6}
                  pathOptions={{ color: "#0A0D0C", weight: 2, fillColor: "#457B9D", fillOpacity: 0.9 }}
                />
              ))}
              {photoPins.map((p) => (
                <CircleMarker
                  key={p.file_id}
                  center={[p.latitude, p.longitude]}
                  radius={7}
                  pathOptions={{ color: "#0A0D0C", weight: 2, fillColor: "#E9C46A", fillOpacity: 1 }}
                >
                  <Popup maxWidth={220}>
                    <div style={{ width: 200 }}>
                      <img
                        src={fileUrl(p.file_id)}
                        alt="Field photo"
                        data-testid={`photo-pin-img-${p.file_id}`}
                        style={{ width: "100%", height: 120, objectFit: "cover", cursor: "pointer", borderRadius: 2 }}
                        onClick={() => setLightbox(fileUrl(p.file_id))}
                      />
                      <div style={{ fontSize: 11, fontWeight: 700, marginTop: 6 }}>
                        {p.author}{p.verified ? " · Verified" : ""}
                      </div>
                      <div style={{ fontSize: 11, opacity: 0.75 }}>{relTime(p.created_at)}</div>
                      {p.message && (
                        <div style={{ fontSize: 11, marginTop: 4 }}>
                          {p.message.length > 90 ? `${p.message.slice(0, 90)}…` : p.message}
                        </div>
                      )}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-[15px] text-tac-dim">No coordinates available</div>
          )}
          {coordsValid && (
            <div className="absolute top-3 left-14 z-[1000] flex flex-col items-start gap-1.5">
              {mapExtras.toolbar}
              {oldViirsCount > 0 && (
                <button
                  type="button"
                  data-testid="viirs-age-toggle"
                  onClick={() => setShowOldViirs((v) => !v)}
                  className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border backdrop-blur-xl transition-colors ${
                    showOldViirs
                      ? "bg-tac-primary/20 border-tac-primary text-tac-primary"
                      : "bg-tac-bg/85 border-tac-border text-tac-dim hover:border-tac-borderBright"
                  }`}
                >
                  Older hotspots 24–48h ({oldViirsCount})
                </button>
              )}
              {allViirs.length > 0 && (
                <button
                  type="button"
                  data-testid="viirs-all-toggle"
                  title="Show every satellite hotspot in the region (last 48h), not just near this fire"
                  onClick={() => setShowAllHotspots((v) => !v)}
                  className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border backdrop-blur-xl transition-colors ${
                    showAllHotspots
                      ? "bg-tac-primary/20 border-tac-primary text-tac-primary"
                      : "bg-tac-bg/85 border-tac-border text-tac-dim hover:border-tac-borderBright"
                  }`}
                >
                  All hotspots ({allViirs.length})
                </button>
              )}
              {nearbyEvacs.length > 0 && (
                <button
                  type="button"
                  data-testid="evac-zones-toggle"
                  onClick={() => setShowEvacZones((v) => !v)}
                  className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border backdrop-blur-xl transition-colors ${
                    showEvacZones
                      ? "bg-tac-bg/85 border-tac-border text-tac-text hover:border-tac-borderBright"
                      : "bg-tac-bg/85 border-tac-border text-tac-faint hover:border-tac-borderBright"
                  }`}
                >
                  Evac zones {showEvacZones ? "on" : "off"}
                </button>
              )}
            </div>
          )}
          <EvacClosuresStrip
            evac={popupEvac}
            closures={nearbyClosures}
            wsdotAlerts={wsdotAlerts}
            onRoadClick={(lat, lon) => setStripFocus([lat, lon])}
          />
        </div>
      </div>

      {incident.web_comment && (
        <div className="border border-tac-border bg-tac-surface/40 p-4 mb-6">
          <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-2">
            Dispatch Comment
          </div>
          <p className="text-sm text-tac-text leading-relaxed whitespace-pre-line">{incident.web_comment}</p>
        </div>
      )}

      {incident.resources && incident.resources.length > 0 && (
        <div className="border border-tac-border bg-tac-surface/40 p-4 mb-6" data-testid="incident-resources">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
              Resources Assigned
            </div>
            <div className="text-[15px] font-mono-tac tabular text-tac-dim">
              {incident.resources.length} {incident.resources.length === 1 ? "unit" : "units"}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {incident.resources.map((r) => (
              <span
                key={r}
                title="Resource unit assigned by dispatch"
                className="inline-flex items-center px-2 py-1 text-[11px] font-bold font-mono-tac tabular tracking-wider uppercase border border-tac-borderBright bg-tac-bg text-tac-text rounded-sm"
              >
                {r}
              </span>
            ))}
          </div>
          <p className="mt-2 text-[12px] font-mono-tac text-tac-faint">
            Live from WildCAD-E dispatch. Codes follow standard wildland resource conventions (e.g. E = engine, BR = brush, IC = incident commander, T = type-X aircraft).
          </p>
        </div>
      )}

      {/* Field Updates · Operations Log */}
      <div className="border border-tac-border bg-tac-surface/40 mb-6">
        <div className="flex items-center justify-between px-4 py-3 border-b border-tac-border">
          <div className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
            Field Updates · Operations Log
          </div>
          <div className="text-[15px] font-mono-tac tabular text-tac-dim">
            {updates.length} {updates.length === 1 ? "entry" : "entries"}
          </div>
        </div>

        <form onSubmit={handlePost} className="p-4 border-b border-tac-border" data-testid="update-form">
          {user && user !== false ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-tac-primary/55 bg-tac-primary/15 text-tac-primary rounded-sm">
                  <ShieldCheck size={9} weight="fill" /> Verified
                </span>
                <span className="text-[15px] uppercase tracking-wider text-tac-dim font-bold">
                  Posting as {user.display_name}
                </span>
              </div>
              <div className="grid sm:grid-cols-[1fr_auto] gap-2">
                <textarea
                  data-testid="update-message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={2}
                  placeholder="Post a field observation, conditions, or sighting… (Enter for a new paragraph)"
                  className="bg-tac-bg border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[15px] text-tac-text placeholder:text-tac-faint outline-none resize-y min-h-[42px]"
                />
                <button
                  data-testid="update-submit"
                  type="submit"
                  disabled={!message.trim() || posting}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 text-[10px] font-bold uppercase tracking-wider bg-tac-primary text-white rounded-sm hover:bg-tac-primary/90 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition"
                >
                  <PaperPlaneTilt size={12} weight="fill" /> {posting ? "Posting…" : "Post"}
                </button>
              </div>
              {canAttach && (
                <div className="mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={handleFiles}
                    data-testid="update-attach-input"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      data-testid="update-attach-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || attachments.length >= 4}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:border-tac-primary text-tac-dim hover:text-tac-text rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {uploading ? (
                        <CircleNotch size={12} className="animate-spin" />
                      ) : (
                        <Camera size={12} weight="bold" />
                      )}
                      {uploading ? "Uploading…" : `Attach Photos (${attachments.length}/4)`}
                    </button>
                    {attachments.map((a) => (
                      <div key={a.id} className="relative group" data-testid={`attachment-thumb-${a.id}`}>
                        <img
                          src={fileUrl(a.id)}
                          alt={a.name}
                          className="h-12 w-12 object-cover border border-tac-border rounded-sm"
                        />
                        {a.geotagged && (
                          <span
                            title="Geotagged — will appear as a pin on the map"
                            data-testid={`attachment-geotag-${a.id}`}
                            className="absolute bottom-0 left-0 bg-black/70 text-[#E9C46A] p-0.5"
                          >
                            <MapPin size={9} weight="fill" />
                          </span>
                        )}
                        <button
                          type="button"
                          data-testid={`attachment-remove-${a.id}`}
                          onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                          className="absolute -top-1.5 -right-1.5 bg-tac-danger text-white rounded-full p-0.5 hover:scale-110 transition-transform"
                          title="Remove photo"
                        >
                          <X size={10} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {postError && (
                <div data-testid="update-error" className="text-[11px] text-tac-danger mt-2">
                  {postError}
                </div>
              )}
              <div className="text-[15px] text-tac-faint mt-2 font-mono-tac">
                Community-sourced. Always cross-reference with official agency updates.
                {canAttach ? " Photos with location data (EXIF GPS or your device location) drop as pins on the map." : ""}
              </div>
            </>
          ) : (
            <div data-testid="update-login-prompt" className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-tac-text">
                  Log in to post field updates
                </div>
                <div className="text-[14px] text-tac-dim mt-0.5">
                  Verified reporter updates carry more weight in the operations log.
                </div>
              </div>
              <button
                data-testid="update-login-btn"
                onClick={() => navigate(`/login?next=${encodeURIComponent(`/incident/${id}`)}`)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-primary text-tac-text rounded-sm transition-colors"
              >
                <SignIn size={12} weight="bold" /> Log in
              </button>
            </div>
          )}
        </form>

        <div className="divide-y divide-tac-border/60">
          {updates.length === 0 ? (
            <div className="p-8 text-center text-[15px] text-tac-faint">
              No field updates yet. Be the first to post one.
            </div>
          ) : (
            updates.map((u) => (
              <div key={u.id} data-testid={`update-${u.id}`} className="px-4 py-3 flex items-start gap-4">
                <div className="font-mono-tac tabular text-[15px] text-tac-faint w-24 shrink-0 uppercase tracking-wider pt-0.5">
                  {relTime(u.created_at)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-tac-primary">
                      {u.author}
                    </span>
                    {u.verified && (
                      <span
                        title="Verified reporter"
                        className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider border border-tac-primary/55 bg-tac-primary/15 text-tac-primary rounded-sm"
                      >
                        <ShieldCheck size={8} weight="fill" /> Verified
                      </span>
                    )}
                  </div>
                  {editingId === u.id ? (
                    <div className="mt-1">
                      <textarea
                        data-testid={`update-edit-input-${u.id}`}
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        rows={3}
                        className="w-full bg-tac-bg border border-tac-border focus:border-tac-primary rounded-sm px-3 py-2 text-[15px] text-tac-text outline-none resize-y"
                      />
                      <div className="flex gap-2 mt-1.5">
                        <button
                          type="button"
                          data-testid={`update-edit-save-${u.id}`}
                          onClick={saveEdit}
                          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-tac-primary text-black rounded-sm hover:opacity-90 transition-opacity"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          data-testid={`update-edit-cancel-${u.id}`}
                          onClick={() => setEditingId(null)}
                          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-tac-border text-tac-dim rounded-sm hover:text-tac-text transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-tac-text leading-relaxed">
                      {toParagraphs(u.message).map((p, pi) => (
                        <p key={pi} className="mb-2 last:mb-0">{p}</p>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <button
                      type="button"
                      data-testid={`update-heart-${u.id}`}
                      onClick={() => toggleHeart(u.id)}
                      title={user ? "Mark as read" : "Log in to mark as read"}
                      className={`inline-flex items-center gap-1 text-[12px] font-bold transition-colors ${
                        u.hearts?.some((h) => h.user_id === user?.id)
                          ? "text-tac-danger"
                          : "text-tac-faint hover:text-tac-danger"
                      }`}
                    >
                      <Heart
                        size={13}
                        weight={u.hearts?.some((h) => h.user_id === user?.id) ? "fill" : "regular"}
                      />
                      {u.hearts?.length || 0}
                    </button>
                    {u.hearts?.length > 0 && (
                      <span
                        data-testid={`update-readers-${u.id}`}
                        className="text-[11px] text-tac-faint"
                        title={u.hearts.map((h) => h.name).join(", ")}
                      >
                        Read by {u.hearts.slice(0, 3).map((h) => h.name).join(", ")}
                        {u.hearts.length > 3 ? ` +${u.hearts.length - 3} more` : ""}
                      </span>
                    )}
                    {u.edited_at && (
                      <span className="text-[10px] uppercase tracking-wider text-tac-faint">edited</span>
                    )}
                    {user && (user.id === u.user_id || user.role === "admin") && editingId !== u.id && (
                      <>
                        <button
                          type="button"
                          data-testid={`update-edit-${u.id}`}
                          onClick={() => {
                            setEditingId(u.id);
                            setEditText(u.message);
                          }}
                          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-tac-faint hover:text-tac-text transition-colors"
                        >
                          <PencilSimple size={11} weight="bold" /> Edit
                        </button>
                        <button
                          type="button"
                          data-testid={`update-delete-${u.id}`}
                          onClick={() => deleteUpdate(u.id)}
                          className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-tac-faint hover:text-tac-danger transition-colors"
                        >
                          <Trash size={11} weight="bold" /> Delete
                        </button>
                      </>
                    )}
                  </div>
                  {u.image_ids?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {u.image_ids.map((fid) => (
                        <button
                          key={fid}
                          type="button"
                          data-testid={`update-image-${fid}`}
                          onClick={() => setLightbox(fileUrl(fid))}
                          className="block border border-tac-border hover:border-tac-primary rounded-sm overflow-hidden transition-colors"
                          title="Click to enlarge"
                        >
                          <img
                            src={fileUrl(fid)}
                            alt="Field update attachment"
                            loading="lazy"
                            className="h-24 w-24 sm:h-28 sm:w-28 object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {lightbox && (
        <div
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[1200] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lightbox-close"
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            onClick={() => setLightbox(null)}
          >
            <X size={24} weight="bold" />
          </button>
          <img
            src={lightbox}
            alt="Field update full size"
            className="max-h-[90vh] max-w-[95vw] object-contain rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Nearest Wildfire Cameras */}
      {cameras.length > 0 && (
        <div className="border border-tac-border bg-tac-surface/40 mb-6" data-testid="nearest-cameras">
          <div className="px-4 py-3 border-b border-tac-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <VideoCamera size={14} weight="duotone" className="text-tac-info" />
              <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
                Nearest Wildfire Cameras
              </span>
            </div>
            <Link to="/cameras" data-testid="all-cameras-link" className="text-[15px] font-bold uppercase tracking-wider text-tac-dim hover:text-tac-primary">
              View All →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 p-3">
            {cameras.map((cam) => (
              <CameraCard key={cam.id} camera={cam} dense />
            ))}
          </div>
          <div className="px-4 pb-3 text-[15px] font-mono-tac text-tac-faint">
            Click to open in WA DNR Wildfire Watch viewer (external).
          </div>
        </div>
      )}

      {incident.county && (
        <div className="border border-tac-border mb-6" data-testid="incident-scanner">
          <ScannerFeedsCard
            countySlug={incident.county}
            countyName={incident.county
              .replace("_", " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())}
          />
        </div>
      )}

      {deepLinkedCamera && (
        <CameraViewerModal
          camera={deepLinkedCamera}
          onClose={() => setDeepLinkedCamera(null)}
        />
      )}

      <SponsorRotator variant="strip" />
    </div>
  );
}

function TeamsOnGroundStrip({ match }) {
  return (
    <div
      data-testid="teams-on-ground-strip"
      className="border border-tac-border bg-tac-surface/60 mb-6 px-4 py-3"
    >
      <div className="flex items-center gap-2 mb-1">
        <Users size={15} weight="bold" className="text-tac-primary shrink-0" />
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-tac-primary">
          Teams on the Ground
        </span>
      </div>
      <div data-testid="teams-on-ground-text" className="text-sm text-tac-text">
        <b>{match.team}</b> is assigned to this fire
        {match.location ? <> — {match.location}</> : null}.
      </div>
      <div className="mt-1 text-[11px] font-mono-tac text-tac-faint">
        Source: NWCC Morning Brief · Public Information:{" "}
        <a href="tel:+15038082764" data-testid="teams-pio-phone" className="text-tac-dim hover:text-tac-primary underline">
          (503) 808-2764
        </a>
      </div>
    </div>
  );
}

function GrowthEstimateStrip({ growth, nifcAcres }) {
  const passTime = growth.latest_pass
    ? new Date(growth.latest_pass).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : null;
  return (
    <div
      data-testid="growth-estimate-strip"
      className="border border-tac-primary/50 bg-tac-primary/10 mb-6 px-4 py-3"
    >
      <div className="flex items-center gap-2 mb-1">
        <TrendUp size={15} weight="bold" className="text-tac-primary shrink-0" />
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-tac-primary">
          Satellite Growth Estimate
        </span>
      </div>
      <div data-testid="growth-estimate-text" className="text-sm text-tac-text">
        Fresh satellite hotspots (last {growth.window_hours} hrs) extend{" "}
        <b>~{growth.est_new_acres.toLocaleString()} acres</b> beyond the mapped perimeter
        {growth.direction ? <> — spreading <b>{growth.direction}</b></> : null}.
        {" "}Estimated total <b>~{growth.est_total_acres.toLocaleString()} acres</b>{" "}
        (<span data-testid="growth-mapped-acres">{growth.mapped_acres.toLocaleString()} ac mapped</span>
        {nifcAcres != null ? (
          <span data-testid="growth-nifc-acres"> · NIFC reports {Math.round(nifcAcres).toLocaleString()} ac</span>
        ) : null}).
      </div>
      <div className="mt-1 text-[11px] font-mono-tac text-tac-faint">
        {growth.hotspots_outside} of {growth.hotspots_recent} detections outside the line
        {passTime ? <> · latest pass {passTime}</> : null} · satellite estimate, not an official figure
        {" "}· "mapped" = latest GIS fire-perimeter polygon; NIFC = federal incident record (often lags the map)
      </div>
    </div>
  );
}

function PanTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, Math.max(map.getZoom(), 11), { duration: 0.8 });
  }, [position, map]);
  return null;
}

function FitToPin({ fire, pin }) {
  const map = useMap();
  useEffect(() => {
    if (pin && fire) {
      map.fitBounds([[fire[0], fire[1]], [pin.lat, pin.lon]], { padding: [50, 50], maxZoom: 12 });
    }
  }, [pin?.lat, pin?.lon]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function Stat({ icon: Icon, label, value, mono, note }) {
  return (
    <div className="px-4 py-3 border-r last:border-r-0 border-tac-border">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={11} weight="fill" className="text-tac-primary" />
        <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint">{label}</span>
      </div>
      <div className={`text-tac-text text-sm font-semibold tabular ${mono ? "font-mono-tac" : ""}`}>
        {value}
      </div>
      {note && (
        <div className="text-[10px] font-mono-tac text-tac-faint mt-0.5 leading-tight" data-testid="stat-note">
          {note}
        </div>
      )}
    </div>
  );
}

function StatusRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-tac-dim">{label}</span>
      <span className={`font-mono-tac tabular text-[15px] ${value ? "text-tac-safe" : "text-tac-faint"}`}>
        {value ? formatDate(value) : "Pending"}
      </span>
    </div>
  );
}

const CAUSE_LABELS = { U: "Undetermined", H: "Human", L: "Lightning" };

function NifcKv({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="px-4 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint mb-0.5">{label}</div>
      <div className="text-sm font-semibold text-tac-text">{value}</div>
    </div>
  );
}

function AcresSparkline({ incidentId, nifcId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchAcresHistory(incidentId, nifcId)
      .then((r) => { if (alive) setRows(r); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [incidentId, nifcId]);

  if (rows === null) return null;
  // Prefer mapped-perimeter acreage (most current), then NIFC, then all.
  const perimRows = rows.filter((r) => r.source === "perimeter");
  const nifcRows = rows.filter((r) => r.source === "nifc");
  const series = perimRows.length >= 2 ? perimRows : nifcRows.length >= 2 ? nifcRows : rows;
  const spanMs =
    series.length >= 2
      ? new Date(series[series.length - 1].at) - new Date(series[0].at)
      : 0;
  // Need at least two points spread over 30+ minutes to draw a trend.
  if (series.length < 2 || spanMs < 30 * 60 * 1000) {
    return (
      <div className="px-4 pt-3 text-[11px] font-mono-tac text-tac-faint" data-testid="acres-sparkline-pending">
        Growth tracking active — the 7-day acreage trend will appear here as updates arrive.
      </div>
    );
  }

  const W = 240, H = 44, PAD = 3;
  const vals = series.map((r) => r.acres);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const t0 = new Date(series[0].at).getTime();
  const t1 = new Date(series[series.length - 1].at).getTime();
  const tspan = t1 - t0 || 1;
  const pts = series.map((r) => {
    const x = PAD + ((new Date(r.at).getTime() - t0) / tspan) * (W - PAD * 2);
    const y = H - PAD - ((r.acres - min) / span) * (H - PAD * 2);
    return [x, y];
  });
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${PAD},${H - PAD} ${line} ${(W - PAD).toFixed(1)},${H - PAD}`;
  const delta = vals[vals.length - 1] - vals[0];
  const growing = delta > 0.05;
  const color = growing ? "#F2542D" : "#2A9D8F";

  return (
    <div className="px-4 pt-3" data-testid="acres-sparkline">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint">
          Acreage · 7-Day Trend
        </span>
        <span className="text-[11px] font-bold font-mono-tac tabular" style={{ color }}>
          {growing ? "▲" : delta < -0.05 ? "▼" : "•"} {delta >= 0 ? "+" : ""}{formatAcres(Math.round(delta * 10) / 10)} ac
        </span>
      </div>
      <div className="flex items-end gap-3">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[340px] h-11" preserveAspectRatio="none">
          <polygon points={area} fill={color} opacity="0.13" />
          <polyline points={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
          <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.6" fill={color} stroke="#0F1311" strokeWidth="1.2" />
        </svg>
        <div className="shrink-0 text-right">
          <div className="text-sm font-bold font-mono-tac tabular text-tac-text">{formatAcres(vals[vals.length - 1])} ac</div>
          <div className="text-[10px] font-mono-tac text-tac-faint">from {formatAcres(vals[0])} ac</div>
          <div className="text-[10px] font-mono-tac text-tac-faint" data-testid="acres-updated-stamp">
            upd {new Date(series[series.length - 1].at).toLocaleString("en-US", { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </div>
        </div>
      </div>
    </div>
  );
}

function NifcFederalCard({ nifc, incidentId }) {
  const pct = nifc.percent_contained;
  return (
    <div className="border border-tac-border bg-tac-surface/40 mb-6" data-testid="nifc-federal-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-tac-border flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} weight="duotone" className="text-tac-primary" />
          <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
            Federal Incident Data · NIFC
          </span>
        </div>
        {(nifc.unique_fire_id || nifc.id) && (
          <span className="text-[11px] font-mono-tac tabular text-tac-faint uppercase tracking-wider">
            {nifc.unique_fire_id || ""}
          </span>
        )}
      </div>

      <AcresSparkline incidentId={incidentId} nifcId={nifc.id} />

      {pct != null && (
        <div className="px-4 pt-3" data-testid="nifc-containment-bar">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint">Containment</span>
            <span className={`text-sm font-bold font-mono-tac tabular ${pct >= 100 ? "text-tac-safe" : "text-tac-primary"}`}>
              {Math.round(pct)}%
            </span>
          </div>
          <div className="h-2 bg-tac-bg border border-tac-border rounded-sm overflow-hidden">
            <div
              className={`h-full ${pct >= 100 ? "bg-tac-safe" : "bg-tac-primary"} transition-all`}
              style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-x divide-y divide-tac-border/40 mt-1">
        <NifcKv label="Size (NIFC)" value={nifc.incident_size != null ? `${formatAcres(nifc.incident_size)} ac` : null} />
        <NifcKv label="Cause" value={CAUSE_LABELS[nifc.cause] || nifc.cause} />
        <NifcKv label="Discovered" value={nifc.discovered_at ? formatDate(nifc.discovered_at) : null} />
        <NifcKv label="Last Updated" value={nifc.modified_at ? formatDate(nifc.modified_at) : null} />
        <NifcKv label="Protecting Agency" value={nifc.protecting_agency} />
        <NifcKv label="Jurisdiction" value={nifc.jurisdictional_agency} />
        <NifcKv label="Dispatch Center" value={nifc.dispatch_center} />
        <NifcKv label="GACC" value={nifc.gacc} />
        <NifcKv label="Management" value={nifc.management_org} />
        <NifcKv label="County (NIFC)" value={nifc.county} />
      </div>

      {nifc.short_description && (
        <p className="px-4 py-2.5 text-[13px] text-tac-dim leading-relaxed border-t border-tac-border/40">
          {nifc.short_description}
        </p>
      )}
      <div className="px-4 pb-3 pt-1 text-[11px] font-mono-tac text-tac-faint">
        Source: NIFC WFIGS (federal GIS) — updates may lag dispatch by hours.
      </div>
    </div>
  );
}

